/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import axios from 'axios';
import appsUtil from '../utils/AppsUtil';
import SophiaUtil from '../utils/SophiaUtil';
import {FETCH_CATALOG_BANNERS_DATA} from '../types';
import {CVSContentfulLocaleMap, campaignId} from '../constants/appsConstants';
import {isTestAExperience} from '../utils/TestUtil';
import Url from '../utils/Url';

function useNull(e) {
	console.log(e);
	return null;
}

function getPlatformIndex() {
	const platform = appsUtil.getCCWebPathData().subSectionId;
	switch (platform) {
		case 'desktop':
			return 1;
		case 'mobile':
			return 2;
		case 'web':
			return 3;
		default:
			return null;
	}
}

export const getCatalogBannersDataForCCXUI = (inputLocale: string, imsData: Object) => async(dispatch: Function) => {
	appsUtil.setImsData(imsData);
	const ALL_APPS_BANNERS_SURFACE_ID = 'ccw_all_apps_banners';
	const ALL_APPS_BANNERS_SURFACE_ID_AEM = 'CCH_ALL_APPS_BANNERS';

	let allAppsBannerEndPoint;

	if (!isTestAExperience(campaignId.bannerOnMobile)) {
		try {
			allAppsBannerEndPoint = await SophiaUtil.extractCVSEndPointFromSophia(appsUtil.hasFeature('temp_contentful_to_aem_migration') ? ALL_APPS_BANNERS_SURFACE_ID_AEM : ALL_APPS_BANNERS_SURFACE_ID, inputLocale);
		}
		catch (err) {}
	}
	return dispatch(fetchAndDispatchBannerData(allAppsBannerEndPoint));
};

export const getCatalogBannersData = (allAppsBannerEndPoint: ?string, inputLocale: string) => async(dispatch: Function) => {
	let allAppsBannerURL = allAppsBannerEndPoint;
	const contentfulLocal = CVSContentfulLocaleMap[inputLocale];

	if (allAppsBannerURL) {
		allAppsBannerURL = allAppsBannerURL.substring(0, allAppsBannerURL.lastIndexOf('/'));
		allAppsBannerURL = `${allAppsBannerURL}/${contentfulLocal}.json`;
	}

	return dispatch(fetchAndDispatchBannerData([allAppsBannerURL]));
};

const fetchAndDispatchBannerData = (allAppsBannerEndPoint: ?Object) => async(dispatch: Function) => {
	if (appsUtil.hasFeature('temp_contentful_to_aem_migration')) {
		const allPromisesArray = [];
		const bannerUrl = Url.getParam('bannerPreview');
		const domain = bannerUrl && bannerUrl.split('/').length > 3 ? bannerUrl.split('/')[2] : '';
		if (bannerUrl && (domain === 'odinpreview.corp.adobe.com' || domain === 'odin.adobe.com')) {
			const index = getPlatformIndex();
			if (index) {
				allAppsBannerEndPoint[index - 1] = bannerUrl;
			}
		}
		if (allAppsBannerEndPoint) {
			allAppsBannerEndPoint.forEach((item) => {
				if (typeof item !== 'undefined') {
					allPromisesArray.push(axios.get(item, {timeout: 10000}).catch(useNull));
				}
			});
		}
		else {
			allPromisesArray.push(Promise.resolve(null), Promise.resolve(null), Promise.resolve(null));
		}
		return Promise.all(allPromisesArray).then(axios.spread((allAppsBannerDesktop, allAppsBannerMobile, allAppsBannerWeb) => {
			dispatch({
				type: FETCH_CATALOG_BANNERS_DATA,
				payload: {allAppsBannerDesktop, allAppsBannerMobile, allAppsBannerWeb}
			});
		}));
	}

	let allAppsPromise;
	if (allAppsBannerEndPoint && allAppsBannerEndPoint[0]) {
		allAppsPromise = axios.get(allAppsBannerEndPoint[0]).catch(useNull);
	}
	else {
		allAppsPromise = Promise.resolve(null);
	}
	return allAppsPromise.then((allAppsBanner) => {
		dispatch({
			type: FETCH_CATALOG_BANNERS_DATA,
			payload: {allAppsBanner}
		});
	});
};
